import React, { useState } from 'react';
import { Facebook, Twitter, Instagram, Linkedin, Phone, Mail, Zap } from 'lucide-react';
import { validation } from '../../utils/validation';
import { notifications } from '../../utils/notifications';

const Footer = ({ categories, onScrollToSection, onSelectCategory }) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!validation.email(newsletterEmail)) {
      notifications.showError('Please enter a valid email address');
      return;
    }
    // TODO: wire to a real newsletter subscription endpoint once one exists.
    notifications.showSuccess('You\'re on the list! Watch your inbox for new arrivals.');
    setNewsletterEmail('');
  };

  return (
    <footer className="bg-ink text-paper pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-accent rounded-full flex items-center justify-center">
              <Zap className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display text-xl">DPC Collection</span>
          </div>
          <p className="text-paper/60 text-sm leading-relaxed mb-5">
            Handcrafted bags and shoes, made for the everyday carry.
          </p>
          <div className="flex items-center gap-3 mb-5">
            {[
              { Icon: Facebook, href: 'https://facebook.com/digitalpulseconsultancy' },
              { Icon: Twitter, href: 'https://twitter.com/digitalpulseke' },
              { Icon: Instagram, href: 'https://instagram.com/digitalpulseke' },
              { Icon: Linkedin, href: 'https://linkedin.com/company/digitalpulseconsultancy' },
            ].map(({ Icon, href }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full border border-paper/20 flex items-center justify-center hover:border-brass hover:text-brass transition-colors"
              >
                <Icon className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
          <div className="space-y-1.5 text-sm text-paper/60">
            <a href="tel:+254111566826" className="flex items-center gap-2 hover:text-paper transition-colors">
              <Phone className="w-3.5 h-3.5" /> +254 111 566 826
            </a>
            <a href="mailto:info@digitalpulseke.com" className="flex items-center gap-2 hover:text-paper transition-colors">
              <Mail className="w-3.5 h-3.5" /> info@digitalpulseke.com
            </a>
          </div>
        </div>

        <div>
          <p className="dpc-label text-paper/50 mb-4">Explore</p>
          <ul className="space-y-2.5 text-sm">
            {[
              { label: 'About us', section: 'about' },
              { label: 'Shop', section: 'products' },
              { label: 'Stories', section: 'testimonials' },
              { label: 'Contact', section: 'contact' },
            ].map((item) => (
              <li key={item.label}>
                <button onClick={() => onScrollToSection(item.section)} className="text-paper/70 hover:text-paper transition-colors">
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="dpc-label text-paper/50 mb-4">Categories</p>
          <ul className="space-y-2.5 text-sm">
            {categories.length === 0 ? (
              <li className="text-paper/40">New categories coming soon</li>
            ) : (
              categories.map((cat) => (
                <li key={cat.id}>
                  <button onClick={() => onSelectCategory(cat.id)} className="text-paper/70 hover:text-paper transition-colors">
                    {cat.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        <div>
          <p className="dpc-label text-paper/50 mb-4">Stay in the loop</p>
          <p className="text-sm text-paper/60 mb-4">New arrivals and the occasional offer. Nothing else.</p>
          <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 min-w-0 px-3.5 py-2.5 text-sm rounded-full bg-paper/10 border border-paper/20 text-paper placeholder:text-paper/40 focus:outline-none focus:border-brass"
            />
            <button type="submit" className="px-4 py-2.5 bg-brass text-ink dpc-label text-[11px] rounded-full hover:bg-paper transition-colors">
              Join
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 border-t border-paper/10 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-xs text-paper/40">
        <span>&copy; {new Date().getFullYear()} DigitalPulse Consultancy LLC. All rights reserved.</span>
        <span className="flex items-center gap-1.5">
          Built by
          <img src="/images/digitalpulse-consultancy-mark.png" alt="DigitalPulse Consultancy" className="w-4 h-4 object-contain" />
          DigitalPulse Consultancy
        </span>
      </div>
    </footer>
  );
};

export default Footer;
